import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Exam, ExamPart } from '../../../model/response';
import { Modal, ModalProps, Switch } from 'antd';
import { findAllExamPartsByExamId } from '../../../service/examPartService';
import processError from '../../../utils/processError';
import LoadingArea from '../../common/LoadingArea';
import ExamPartView from '../../common/ExamPartView';
import { getFirstExamPartQuestion, processExamPartQuestionIndexMap } from '../../../utils/utils';

interface ExamPreviewModalProps extends Omit<ModalProps, 'className' | 'footer' | 'centered' | 'onOk'> {
  exam?: Exam | null;
}

function ExamPreviewModal({ exam, ...restProps }: ExamPreviewModalProps) {
  const [fetching, setFetching] = useState(false);
  const [examParts, setExamParts] = useState<ExamPart[]>([]);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    if (exam?.id) {
      setFetching(true);
      findAllExamPartsByExamId(exam.id)
        .then((result) => {
          result = result.sort((e1, e2) => e1.examPartIndex - e2.examPartIndex);
          result.forEach((item) => {
            item.examPartQuestions = item.examPartQuestions.sort(
              (e1, e2) => e1.examPartQuestionIndex - e2.examPartQuestionIndex,
            );
          });
          setExamParts(result);
        })
        .catch(processError)
        .finally(() => setFetching(false));
    }
  }, [exam]);

  useLayoutEffect(() => {
    if ((window as any).MathJax) {
      const renderMathJax = () => {
        const win: any = window;
        win.MathJax.typesetPromise()
          .then(() => {
            console.log('MathJax rendering complete');
          })
          .catch((err: any) => {
            console.error('Error rendering MathJax: ', err);
          });
      };

      setTimeout(renderMathJax, 800);
    }
  }, [restProps.open]);

  const firstExamPartQuestionId = useMemo(() => getFirstExamPartQuestion(examParts)?.id || null, [examParts]);
  const examPartQuestionIndexMap = useMemo(() => processExamPartQuestionIndexMap(examParts), [examParts]);

  return (
    <Modal centered={true} footer={[]} className="w-full min-w-[60vw] rounded-full" {...restProps}>
      <div className={'flex items-center gap-x-2'}>
        <h5>Hiển thị lời giải</h5>
        <Switch value={showGuide} onChange={(e) => setShowGuide(e)} />
      </div>
      <h3 className={'text-xl font-bold underline text-center mb-8'}>
        {showGuide ? 'Lời giải chi tiết' : 'Nội dung đề'}
      </h3>
      <div className={'h-[952px] max-h-[70vh] overflow-y-auto'}>
        <div className={'flex flex-col gap-y-4'}>
          {!fetching ? (
            examParts.map((examPart) => (
              <ExamPartView
                examPart={examPart}
                key={examPart.id}
                firstExamPartQuestionId={firstExamPartQuestionId}
                examPartQuestionIndexMap={examPartQuestionIndexMap}
                showGuide={showGuide}
              />
            ))
          ) : (
            <LoadingArea />
          )}
        </div>
      </div>
    </Modal>
  );
}

export default ExamPreviewModal;
